/**
 * Hausaufgaben (F-22 Stufe 1) - normale Aufgaben mit Fach (Leitlinie 5).
 * Auf der Schulseite fuer alle Faecher, auf der Fachseite fuer eines.
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';

import { Input } from '@/ui/primitives/Input';
import { Button } from '@/ui/primitives/Button';
import { TaskRow } from '@/modules/tasks/components/TaskRow';
import { nachFrist, useHausaufgaben } from '@/modules/school/useSchool';
import { createTask, toggleTaskDone } from '@/db/repositories/tasks';
import { aufgabeLoeschen } from '@/modules/tasks/serienFrage';
import type { Subject, Task } from '@/domain/types';

export function Hausaufgaben({ faecher, areaId }: { faecher: Subject[]; areaId: string | null }) {
  const ids = new Set(faecher.map((f) => f.id));
  const alle = useHausaufgaben().filter((t) => ids.has(t.subjectId!));
  const [erledigteZeigen, setErledigteZeigen] = useState(false);
  const offen = alle.filter((t) => t.status !== 'erledigt').sort(nachFrist);
  const erledigt = alle.filter((t) => t.status === 'erledigt').sort(nachFrist);

  const zeile = (t: Task) => (
    <TaskRow key={t.id} task={t} onToggleDone={() => void toggleTaskDone(t.id)} onDelete={() => void aufgabeLoeschen(t.id)} />
  );

  return (
    <section aria-label="Hausaufgaben" className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3.5">
      <h2 className="text-sm font-semibold">Hausaufgaben ({offen.length} offen)</h2>
      {offen.length === 0
        ? <p className="text-sm text-ink-faint">Keine offenen Hausaufgaben.</p>
        : <ul className="flex flex-col gap-1.5">{offen.map(zeile)}</ul>}
      {faecher.length > 0 && <NeueHausaufgabe faecher={faecher} areaId={areaId} />}
      {erledigt.length > 0 && (
        <div className="flex flex-col gap-1.5 border-t border-line pt-2">
          <button type="button" aria-expanded={erledigteZeigen} onClick={() => setErledigteZeigen((x) => !x)}
            className="self-start text-[12px] text-ink-faint hover:text-ink">
            {erledigteZeigen ? 'Erledigte ausblenden' : `Erledigte anzeigen (${erledigt.length})`}
          </button>
          {erledigteZeigen && <ul className="flex flex-col gap-1.5">{erledigt.map(zeile)}</ul>}
        </div>
      )}
    </section>
  );
}

/** Hausaufgabe anlegen: Titel, Fach, faellig am, Notiz. */
function NeueHausaufgabe({ faecher, areaId }: { faecher: Subject[]; areaId: string | null }) {
  const [titel, setTitel] = useState('');
  const [fach, setFach] = useState('');
  const [faellig, setFaellig] = useState('');
  const [notiz, setNotiz] = useState('');
  const fachId = fach || faecher[0]?.id || '';

  async function anlegen() {
    if (!titel.trim() || !fachId) return;
    await createTask({
      title: titel,
      subjectId: fachId,
      areaId,
      status: 'naechste',
      ...(faellig ? { dueDate: faellig } : {}),
      ...(notiz.trim() ? { notes: notiz.trim() } : {}),
    });
    setTitel(''); setNotiz(''); setFaellig('');
  }

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-2.5">
      <Input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Neue Hausaufgabe"
        aria-label="Neue Hausaufgabe" onKeyDown={(e) => { if (e.key === 'Enter') void anlegen(); }} className="h-9 text-sm" />
      <div className="flex flex-wrap gap-2">
        {faecher.length > 1 && (
          <select value={fachId} onChange={(e) => setFach(e.target.value)} aria-label="Fach"
            className="h-9 rounded-md border border-line bg-surface px-2 text-sm">
            {faecher.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}
        <div className="w-40">
          <Input type="date" value={faellig} onChange={(e) => setFaellig(e.target.value)} aria-label="Fällig am" className="h-9 text-sm" />
        </div>
      </div>
      <textarea value={notiz} onChange={(e) => setNotiz(e.target.value)} rows={2} placeholder="Notiz (optional)" aria-label="Notiz"
        className="w-full resize-y rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none" />
      <Button size="sm" variant="primary" className="self-start" disabled={!titel.trim()} onClick={() => void anlegen()}>
        <Plus className="size-3.5" /> Hausaufgabe
      </Button>
    </div>
  );
}
